import { STATUS_ERROR_MAP } from '../constants/statusErrorMap';
import { SERVER_ERROR, UNEXPECTED_ERROR } from '../constants/errorMessages';
import { toToastSafeMessage } from './serviceError';

/**
 * Maps an error object to a user-friendly login error message.
 *
 * @param {Object} error - The error object received from the failed HTTP request.
 * @returns {string} - A user-friendly error message appropriate for the error.
 */
export const getLoginErrorMessage = (error) => {
  if (!error.response) {
    // Use actual message for custom/network errors (e.g. "Login succeeded but no access_token received")
    return error?.message || UNEXPECTED_ERROR;
  }

  const { status } = error.response;

  return mapErrorCodeToMessage(status);
};

/**
 * Maps an HTTP status code to a user-friendly error message.
 *
 * @param {number} status - The HTTP status code from the server response.
 * @returns {string} - A user-friendly error message appropriate for the status code.
 */
const mapErrorCodeToMessage = (status) => {
  let errorCodeMessage = UNEXPECTED_ERROR;

  if (STATUS_ERROR_MAP[status]) {
    errorCodeMessage = STATUS_ERROR_MAP[status];
  }

  if (status >= 500) {
    errorCodeMessage = SERVER_ERROR;
  }

  return errorCodeMessage;
};

/**
 * Extracts error message from API error response, safe for toasts (never shows raw error codes).
 * Supports shape: { success: false, error: { code, message } } and legacy shapes.
 *
 * @param {Error} error - The error object
 * @param {string} [defaultMessage='An error occurred. Please try again.'] - Default message if no error detail found
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error, defaultMessage = 'An error occurred. Please try again.') => {
  const data = error?.response?.data;
  const err = data?.error;
  const fromErrorObject =
    err && typeof err === 'object' && typeof err.message === 'string' ? err.message : null;
  const raw =
    fromErrorObject ||
    data?.detail ||
    data?.message ||
    (typeof err === 'string' ? err : null) ||
    error?.message ||
    defaultMessage;
  return toToastSafeMessage(raw, defaultMessage);
};
